import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { loadArtworkTexture } from '../artwork-texture.js';

// Shared-rig contract: the light is the buffer's own history; a rig would
// only pollute the loop.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'hyperbolic',
  family: 'shader',
  danceability: 0.80,
  requiresTier: 1,
  energyLevel: 0.78,
  bpmMin: 105,
  bpmMax: 175,
  movement: 0.80,
  tonality: 0.65,
  prefers: { gridStrength: [0.55, 1.0] },
  enabled: true
};

// ============================================================================
// Hyperbolic: Tessel's feedback loop with the fold changed from a WALLPAPER
// group to a HYPERBOLIC tiling {p, q} - p-gons, q around every vertex - of
// which the plane has room for ones ours has not: seven triangles round a
// vertex, five squares, four pentagons. Everything the loop is made of is
// Tessel's, unchanged: the FeedbackBuffer, Echo's three cumulative guards,
// the luminance governor, the masked dither, the hue drift, the track fade,
// the cover injected into the one real cell. Only the fold is new, and the
// gestures the geometry allows.
//
// THE MODEL IS THE BAND, NOT THE DISC. The Poincare disc is the iconic
// picture but it is not full frame. The band model represents the plane as a
// horizontal strip of height PI whose top and bottom edges are infinity: it
// fills the frame by construction, and it is infinitely wide, so the image
// can scroll sideways for ever. From the band to the disc:
//     z_disc = tanh(z_band / 2)        (complex tanh)
// The fold is done in the disc, where a mirror is a circle orthogonal to the
// boundary and a reflection is the INVERSION in that circle:
//     z' = c + r^2 / conj(z - c)
// and the folded point goes back to the band with the complex atanh to be
// read from the previous frame. Toward the band's top and bottom the cells
// shrink without end: that is the signature image.
//
// THE FUNDAMENTAL DOMAIN of {p, q} is the triangle with angles PI/p (at the
// polygon's centre, the disc's origin), PI/q (at a vertex) and PI/2 (at an
// edge's midpoint), bounded by three mirrors: the two diameters at angles 0
// and PI/p, and the polygon's edge, a circle orthogonal to the unit circle.
// The classical construction (hyperbolic right-triangle trigonometry, then
// Euclidean distances r = tanh(d/2)):
//     cosh(AC) = cos(PI/q) / sin(PI/p)          A the origin, C the edge midpoint
//     cosh(AB) = cot(PI/p) cot(PI/q)            B the vertex
//     r_C = tanh(AC/2),  r_B = tanh(AB/2)
//     the edge circle has its centre on the ray at PI/p, at distance
//     R = (1 + r_C^2) / (2 r_C), radius rho = (1 - r_C^2) / (2 r_C),
//     which is orthogonal to the unit circle (R^2 = 1 + rho^2) and passes
//     through C and B - the check `buildTiling` runs.
// A mirror placed by hand instead produces a tiling that does not close and
// seams everywhere; every {p, q} here is verified by rotating a point by
// 2 PI / q about B (a symmetry of the tiling, the product of two of its
// reflections) and folding: the folded point must not move.
//
// THE GESTURES. Scrolling is a horizontal translation of the band - the
// cells near the edges scroll slower than the central ones, the hyperbolic
// perspective. Rotation about a vertex by 2 PI / q is a symmetry of the
// tiling: the colours are re-dealt among the cells while the structure does
// not move. Breathing scales the band's height. And the VERTIGO, which no
// Euclidean tiling can do: a vertical translation of the band, which brings
// the minute cells of the edge up to the centre where they turn immense in a
// beat, while the central ones are pushed to the other edge.
// ============================================================================

// --- the loop (Echo's numbers, unchanged) -----------------------------------
const DECAY_DEFAULT = 0.955;
const DECAY_MIN = 0.85;
const DECAY_MAX = 0.975;
const FLOOR_SUB = 0.004;
const CEILING = 0.92;
const GOVERN_EVERY_FRAMES = 30;
const GOVERN_HIGH = 0.35;
const GOVERN_RESUME = 0.25;
const GOVERN_LOW = 0.05;
const GOVERN_STEP = 0.01;
const LUM_SIZE = 8;
const TRACK_FADE_S = 1.2;
const FADE_DECAY = 0.96;
const BAND_TAU = 0.15;

// --- the tilings -------------------------------------------------------------------
const TILINGS = { '7,3': [7, 3], '5,4': [5, 4], '4,5': [4, 5], '3,7': [3, 7], '6,4': [6, 4] };
const TILING_ORDER = ['7,3', '5,4', '4,5', '3,7', '6,4'];
// Opens and closes on the two that read closest to a tiling of ours.
const CALM_TILINGS = ['6,4', '4,5'];
const DEFAULT_TILING = '7,3';

// --- the transforms ------------------------------------------------------------------
const BREATH = 0.03;                 // +-3% on the band's height, with beatPhase
const BREATH_DECAY = 4.5;
const ZOOM_PER_CELL = 0.988;         // Tessel's local Droste nesting, outward
const SCROLL_CELLS_PER_BAR = 1.0;    // one central cell width per bar
const LOW_SWING = 0.5;               // bands.low on the scroll speed
const ROT_STEP_S = 0.12;
const VERTIGO_BAR = 0.4 * Math.PI;   // the bar's vertigo: up in a beat, back over three
const VERTIGO_DROP = Math.PI;        // the drop's: the edge crosses the frame
const VERTIGO_UP_BEATS = 1, VERTIGO_DOWN_BEATS = 3;
const DROP_DOWN_BARS = 2;

// --- hue ----------------------------------------------------------------------------
const HUE_SHIFT = 0.005;

// --- injection ------------------------------------------------------------------------
const ART_SIZE = 1.15;               // of the central cell's width
const ART_OPACITY = 0.10;
const ART_RESEED_OPACITY = 0.40;
const RESEED_LIFE_S = 0.05;
const SHAPE_LIFE_S = 0.12;
const SHAPE_HOLD_OPACITY = 0.10;

// --- stability and the edges ---------------------------------------------------------
const EDGE_ATTEN = 0.93;             // the top and bottom edges hold infinitely many cells
const EDGE_ATTEN_R = 0.12;           // of the frame's height
const EDGE_DARK = 0.8;               // the display's own darkening at the edge
const EDGE_DARK_R = 0.15;
const JOINT_DARKEN = 0.15;

const TIER = {
  0: { scale: 0.25, tilings: ['7,3', '5,4', '6,4'], iters: 16, joints: false, vertigo: false },
  1: { scale: 0.25, tilings: ['7,3', '5,4', '6,4'], iters: 16, joints: false, vertigo: false },
  2: { scale: 0.5, tilings: TILING_ORDER, iters: 24, joints: true, vertigo: true }
};
const DEFAULT_TIER = 2;
const MAX_ITERS = 24;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function smooth(t) { t = clamp01(t); return t * t * (3 - 2 * t); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

function hueMatrix(angle, out) {
  const c = Math.cos(angle), s = Math.sin(angle);
  const a = 1 / 3, b = Math.sqrt(1 / 3);
  out.set(
    c + (1 - c) * a, a * (1 - c) - b * s, a * (1 - c) + b * s,
    a * (1 - c) + b * s, c + (1 - c) * a, a * (1 - c) - b * s,
    a * (1 - c) - b * s, a * (1 - c) + b * s, c + (1 - c) * a
  );
  return out;
}

// The three mirrors of {p, q}, in the Poincare disc. Exported for the tests.
export function buildTiling(p, q) {
  const A = Math.PI / p, Bq = Math.PI / q;
  const coshAC = Math.cos(Bq) / Math.sin(A);
  const coshAB = (1 / Math.tan(A)) * (1 / Math.tan(Bq));
  if (coshAC <= 1 + 1e-9 || coshAB <= 1 + 1e-9) throw new Error('{' + p + ',' + q + '} is not hyperbolic');
  const AC = Math.acosh(coshAC), AB = Math.acosh(coshAB);
  const rC = Math.tanh(AC / 2), rB = Math.tanh(AB / 2);
  const R = (1 + rC * rC) / (2 * rC), rho = (1 - rC * rC) / (2 * rC);
  const centre = [R * Math.cos(A), R * Math.sin(A)];
  // the closure check: the vertex B lies on the edge circle
  const dB = Math.hypot(rB - centre[0], 0 - centre[1]);
  const BC = Math.acosh(Math.cos(A) / Math.sin(Bq));
  const T = { p, q, angle: A, rC, rB, centre, rho, closes: Math.abs(dB - rho) < 1e-9, AC, AB, BC };
  T.period = findPeriod(T);
  return T;
}

// The band's horizontal axis is the mirror through the polygon's centre and
// a vertex. A translation along it is a symmetry of the tiling only for
// certain lengths (products of two reflections in mirrors perpendicular to
// the axis), and which lengths depends on the parities of p and q. Rather
// than case it out, the smallest symmetric translation is FOUND: among the
// sums of the triangle's sides, the shortest one that leaves every folded
// point where it was. The scroll offset is wrapped by it, so the band
// coordinate never grows - tanh(x/2) at x = 17 is 1 - 2e-8, past what a
// float holds, and the whole frame collapsed into the boundary at 30 s.
function findPeriod(T) {
  const cands = [];
  for (let a = 0; a <= 4; a++) for (let b = 0; b <= 4; b++) for (let c = 0; c <= 4; c++) {
    const t = a * T.AB + b * T.AC + c * T.BC;
    if (t > 1e-6) cands.push(t);
  }
  cands.sort((x, y) => x - y);
  const pts = [];
  let seed = 7;
  const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
  for (let i = 0; i < 40; i++) { const r = Math.sqrt(rnd()) * 0.85, a = rnd() * Math.PI * 2; pts.push([r * Math.cos(a), r * Math.sin(a)]); }
  for (const t of cands) {
    let ok = true;
    for (const z of pts) {
      const w = discToBandCPU(z);
      const z2 = bandToDiscCPU([w[0] + t, w[1]]);
      const f0 = foldDisc(z, T, 40), f1 = foldDisc(z2, T, 40);
      if (f0[2] >= 40 || f1[2] >= 40) continue;
      if (Math.hypot(f0[0] - f1[0], f0[1] - f1[1]) > 1e-6) { ok = false; break; }
    }
    if (ok) return t;
  }
  console.warn('[hyperbolic] no period found for {' + T.p + ',' + T.q + '}, wrapping by 4 AC');
  return 4 * T.AC;
}
export function bandToDiscCPU(w) { const d = Math.cosh(w[0]) + Math.cos(w[1]); return [Math.sinh(w[0]) / d, Math.sin(w[1]) / d]; }
export function discToBandCPU(z) { const nx = 1 + z[0], ny = z[1], dx = 1 - z[0], dy = -z[1]; const dd = dx * dx + dy * dy; const rx = (nx * dx + ny * dy) / dd, ry = (ny * dx - nx * dy) / dd; return [0.5 * Math.log(rx * rx + ry * ry), Math.atan2(ry, rx)]; }

// The fold on the CPU, the shader's arithmetic. Returns [x, y, iterations].
export function foldDisc(z, T, maxIters) {
  let x = z[0], y = z[1];
  const ca = Math.cos(T.angle), sa = Math.sin(T.angle);
  const c2 = Math.cos(2 * T.angle), s2 = Math.sin(2 * T.angle);
  const rho2 = T.rho * T.rho;
  for (let i = 0; i < maxIters; i++) {
    let moved = false;
    if (y < 0) { y = -y; moved = true; }
    if (y * ca - x * sa > 0) { const nx = c2 * x + s2 * y, ny = s2 * x - c2 * y; x = nx; y = ny; moved = true; }
    const dx = x - T.centre[0], dy = y - T.centre[1], dd = dx * dx + dy * dy;
    if (dd < rho2) { const k = rho2 / dd; x = T.centre[0] + dx * k; y = T.centre[1] + dy * k; moved = true; }
    if (!moved) return [x, y, i];
  }
  return [x, y, maxIters];
}
// A rotation about the vertex B, on the CPU (for the tests).
export function rotateAboutVertex(z, rB, phi) {
  // w = (z - B) / (1 - B z), w *= e^{i phi}, z = (w + B) / (1 + B w)
  const nx = z[0] - rB, ny = z[1];
  const dx = 1 - rB * z[0], dy = -rB * z[1];
  const dd = dx * dx + dy * dy;
  let wx = (nx * dx + ny * dy) / dd, wy = (ny * dx - nx * dy) / dd;
  const c = Math.cos(phi), s = Math.sin(phi);
  const tx = wx * c - wy * s, ty = wx * s + wy * c; wx = tx; wy = ty;
  const ax = wx + rB, ay = wy, bx = 1 + rB * wx, by = rB * wy;
  const bb = bx * bx + by * by;
  return [(ax * bx + ay * by) / bb, (ay * bx - ax * by) / bb];
}

const fullscreenVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// The geometry shared by the feedback and display passes: uv -> band ->
// disc, the gestures, the fold, and back to the frame.
function geometryChunk(prec, maxIters) {
  return `
  precision ${prec} float;
  uniform float uAspect;
  uniform float uOffset;       // the scroll, band units
  uniform float uVertigo;      // the vertical shift, band units
  uniform float uBreath;       // the band height's factor
  uniform float uZoom;
  uniform float uRot;          // accumulated rotation about the vertex B
  uniform float uAngle;        // PI / p
  uniform vec2 uMirror;        // the edge circle's centre
  uniform float uMirrorR;      // and radius
  uniform float uVertexR;      // r_B
  uniform int uIters;
  uniform float uDisc;         // 1: the Poincare disc instead of the band (debug)
  const float PI = 3.141592653589793;

  vec2 cmul(vec2 a, vec2 b) { return vec2(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x); }
  vec2 cdiv(vec2 a, vec2 b) { float d = max(dot(b, b), 1e-20); return vec2(a.x * b.x + a.y * b.y, a.y * b.x - a.x * b.y) / d; }
  // tanh(w / 2) = (sinh x + i sin y) / (cosh x + cos y)
  vec2 bandToDisc(vec2 w) {
    float d = cosh(w.x) + cos(w.y);
    return vec2(sinh(w.x), sin(w.y)) / max(d, 1e-6);
  }
  // 2 atanh(z) = log((1 + z) / (1 - z))
  vec2 discToBand(vec2 z) {
    vec2 r = cdiv(vec2(1.0, 0.0) + z, vec2(1.0, 0.0) - z);
    return vec2(0.5 * log(max(dot(r, r), 1e-20)), atan(r.y, r.x));
  }
  vec2 rotateAboutVertex(vec2 z, float phi) {
    vec2 B = vec2(uVertexR, 0.0);
    vec2 w = cdiv(z - B, vec2(1.0, 0.0) - uVertexR * z);
    w = cmul(w, vec2(cos(phi), sin(phi)));
    return cdiv(w + B, vec2(1.0, 0.0) + uVertexR * w);
  }
  // the frame's pixel -> the disc, through every gesture
  vec2 toDisc(vec2 uv) {
    vec2 z;
    if (uDisc > 0.5) {
      z = (uv - 0.5) * vec2(uAspect, 1.0) * 2.04;
    } else {
      vec2 w = (uv - 0.5) * vec2(uAspect, 1.0) * PI;
      w.y *= uBreath;
      w.x -= uOffset;
      w.y += uVertigo;
      // beyond the band's edge the picture continues in mirror: a triangle wave
      float y = mod(w.y + PI * 0.5, PI * 2.0) - PI * 0.5;
      if (y > PI * 0.5) y = PI - y;
      w.y = y;
      z = bandToDisc(w);
    }
    return rotateAboutVertex(z, uRot);
  }
  // the fold into the fundamental triangle: reflections in the two
  // diameters, inversion in the edge circle, until the point stays put
  vec2 foldDisc(vec2 z, out int n, out float dmin) {
    float ca = cos(uAngle), sa = sin(uAngle);
    vec2 e2 = vec2(cos(2.0 * uAngle), sin(2.0 * uAngle));
    float rho2 = uMirrorR * uMirrorR;
    n = ${maxIters};
    for (int i = 0; i < ${maxIters}; i++) {
      if (i >= uIters) break;
      bool moved = false;
      if (z.y < 0.0) { z.y = -z.y; moved = true; }
      if (z.y * ca - z.x * sa > 0.0) { z = cmul(e2, vec2(z.x, -z.y)); moved = true; }
      vec2 d = z - uMirror;
      float dd = dot(d, d);
      if (dd < rho2) { z = uMirror + d * (rho2 / max(dd, 1e-20)); moved = true; }
      if (!moved) { n = i; break; }
    }
    dmin = min(abs(z.y), min(abs(z.y * ca - z.x * sa), abs(length(z - uMirror) - uMirrorR)));
    return z;
  }
  // the folded point, in the canonical cell at the frame's middle
  vec2 toFrame(vec2 zf) {
    vec2 w = discToBand(zf * uZoom);
    return w / (PI * vec2(uAspect, 1.0)) + 0.5;
  }
`;
}

// ============================================================================
// The feedback pass.
// ============================================================================
function feedbackFragmentShader(prec, maxIters) {
  return `
${geometryChunk(prec, maxIters)}
  uniform sampler2D uPrevious;
  uniform sampler2D uSource;
  uniform float uDecay;
  uniform mat3 uHueMat;
  uniform float uSeed;
  uniform vec3 uAverage;       // what a pixel reads past the iteration budget
  varying vec2 vUv;

  void main() {
    vec2 z = toDisc(vUv);
    if (uDisc > 0.5 && dot(z, z) >= 1.0) { gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
    int n; float dmin;
    vec2 zf = foldDisc(z, n, dmin);
    vec2 suv = clamp(toFrame(zf), 0.0, 1.0);
    vec3 prev = n >= uIters ? uAverage : texture2D(uPrevious, suv).rgb;

    prev = uHueMat * prev;

    // the edges hold infinitely many cells and their energy piles up:
    // a little more decay close to them
    float edge = min(vUv.y, 1.0 - vUv.y);
    prev *= mix(${EDGE_ATTEN.toFixed(3)}, 1.0, smoothstep(0.0, ${EDGE_ATTEN_R.toFixed(3)}, edge));

    // STABILITY - Echo's three guards, cumulative and unchanged.
    prev *= uDecay;
    prev -= vec3(${FLOOR_SUB.toFixed(4)});
    prev = max(prev, 0.0);

    // THE INJECTION, sampled at the folded coordinate: the cover IS the
    // cell's content and the tiling replicates it to the edge
    prev += texture2D(uSource, suv).rgb;
    prev = min(prev, vec3(${CEILING.toFixed(3)}));

    // Echo's masked dither, unchanged.
    vec2 fcw = mod(floor(gl_FragCoord.xy) + uSeed, 64.0);
    float dith = (fract(dot(fcw, vec2(0.7548776662, 0.5698402909))) - 0.5) / 255.0;
    float sig = step(3.5 / 255.0, max(prev.r, max(prev.g, prev.b)));
    gl_FragColor = vec4(prev + dith * sig, 1.0);
  }
`;
}

// ============================================================================
// The display pass: the buffer, the edge darkening, the joints (tier 2),
// the seam debug. The fold runs again here at full resolution for the joints
// - never fed back.
// ============================================================================
function displayFragmentShader(prec, maxIters, joints) {
  return `
${geometryChunk(prec, maxIters)}
  uniform sampler2D uBuffer;
  uniform float uPx;           // one screen pixel, of the frame's height
  uniform float uSeamDebug;
  varying vec2 vUv;

  void main() {
    vec3 col = texture2D(uBuffer, vUv).rgb;
    float edge = min(vUv.y, 1.0 - vUv.y);
    col *= mix(${EDGE_DARK.toFixed(2)}, 1.0, smoothstep(0.0, ${EDGE_DARK_R.toFixed(2)}, edge));
    ${joints ? `
    vec2 z = toDisc(vUv);
    int n; float dmin;
    vec2 zf = foldDisc(z, n, dmin);
    // dmin is a Euclidean distance at the folded point; the pixel's own size
    // in the disc is (1 - |z|^2) / 2 * PI * uPx at the ORIGINAL point, and a
    // hyperbolic isometry scales Euclidean lengths by (1 - |zf|^2)/(1 - |z|^2)
    float w = PI * uPx * 0.5 * (1.0 - dot(zf, zf));
    float line = 1.0 - smoothstep(0.0, w, dmin);
    if (uDisc > 0.5 && dot(z, z) >= 1.0) line = 0.0;
    col *= 1.0 - ${JOINT_DARKEN.toFixed(3)} * line;
    if (uSeamDebug > 0.5 && !(uDisc > 0.5 && dot(z, z) >= 1.0)) col = mix(col, vec3(1.0, 0.0, 0.0), (1.0 - smoothstep(0.0, w * 1.5, dmin)) * 0.85);` : `
    if (uSeamDebug > 0.5) {
      vec2 z = toDisc(vUv);
      int n; float dmin;
      vec2 zf = foldDisc(z, n, dmin);
      float w = PI * uPx * 0.5 * (1.0 - dot(zf, zf));
      if (!(uDisc > 0.5 && dot(z, z) >= 1.0)) col = mix(col, vec3(1.0, 0.0, 0.0), (1.0 - smoothstep(0.0, w * 1.5, dmin)) * 0.85);
    }`}
    gl_FragColor = vec4(col, 1.0);
  }
`;
}

const shapeVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const barFragmentShader = `
  precision mediump float;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform vec2 uHalfSize;
  uniform vec2 uRect;
  uniform float uPx;
  varying vec2 vUv;
  void main() {
    vec2 p = abs((vUv - 0.5) * 2.0 * uHalfSize);
    float w = max(uPx * 1.5, 1e-5);
    float a = clamp((uRect.x - p.x) / w, 0.0, 1.0) * clamp((uRect.y - p.y) / w, 0.0, 1.0);
    gl_FragColor = vec4(uColor * (a * uOpacity), 1.0);
  }
`;
const discFragmentShader = `
  precision mediump float;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform vec2 uHalfSize;
  uniform float uInner;
  uniform float uOuter;
  uniform float uPx;
  varying vec2 vUv;
  void main() {
    float r = length((vUv - 0.5) * 2.0 * uHalfSize);
    float w = max(uPx * 1.5, 1e-5);
    float a = clamp((uOuter - r) / w, 0.0, 1.0) * clamp((r - uInner) / w, 0.0, 1.0);
    gl_FragColor = vec4(uColor * (a * uOpacity), 1.0);
  }
`;

export class HyperbolicVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    const t = urlParam('hyptiling');
    this._forcedTiling = t && TILINGS[t] ? t : null;
    const d = parseFloat(urlParam('hypdecay'));
    this._forcedDecay = Number.isFinite(d) ? d : null;
    this._seamDebug = urlParam('hypseam') === '1';
    this._lumDebug = urlParam('hyplum') === '1';
    this._discDebug = urlParam('hypdisk') === '1';
    this._vertigoDebug = urlParam('hypvertigo') === '1';
    this._noJoints = urlParam('hypjoints') === '0';
    const art = parseFloat(urlParam('hypart'));
    this._artOpacity = Number.isFinite(art) ? art : ART_OPACITY;
    // ?hypfreeze=1: no scroll, no notch, no breath, no vertigo - two captures compare
    this._freeze = urlParam('hypfreeze') === '1';
    const prec = urlParam('hypprec');
    this._precision = prec === 'mediump' ? 'mediump' : 'highp';
  }

  init(scene, camera) {
    this._scene = scene;
    const layout = TIER[this._tier];
    this._layout = layout;
    this._buffer = new FeedbackBuffer({ scale: layout.scale });
    this._source = new FeedbackBuffer({ scale: layout.scale, single: true });

    this._feedbackUniforms = {
      uPrevious: { value: null }, uSource: { value: null },
      uAspect: { value: 16 / 9 }, uOffset: { value: 0 }, uVertigo: { value: 0 }, uBreath: { value: 1 },
      uZoom: { value: ZOOM_PER_CELL }, uRot: { value: 0 },
      uAngle: { value: 0 }, uMirror: { value: new THREE.Vector2() }, uMirrorR: { value: 1 }, uVertexR: { value: 0.5 },
      uIters: { value: layout.iters }, uDisc: { value: this._discDebug ? 1 : 0 },
      uDecay: { value: this._forcedDecay != null ? this._forcedDecay : DECAY_DEFAULT },
      uHueMat: { value: hueMatrix(0, new THREE.Matrix3()) }, uSeed: { value: 0 },
      uAverage: { value: new THREE.Vector3(0, 0, 0) }
    };
    this._feedbackScene = new THREE.Scene();
    this._feedbackQuad = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._feedbackUniforms,
        vertexShader: fullscreenVertexShader,
        fragmentShader: feedbackFragmentShader(this._precision, MAX_ITERS),
        depthTest: false, depthWrite: false
      })
    );
    this._feedbackQuad.frustumCulled = false;
    this._feedbackScene.add(this._feedbackQuad);

    // --- injection ---
    this._injectScene = new THREE.Scene();
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    this._artTexture = null;
    this._artMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ transparent: true, opacity: this._artOpacity, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false })
    );
    this._artMesh.visible = false;
    this._injectScene.add(this._artMesh);
    this._shapeMaterials = [];
    this._bar = this._makeBar(0.13, 0.011);
    this._ring = this._makeDisc(0.085, 0.105);
    this._dot = this._makeDisc(0.0, 0.045);
    this._shapes = [this._bar, this._ring, this._dot];
    for (const m of this._shapes) { m.visible = false; this._injectScene.add(m); }

    // --- governor (Echo's, unchanged; it also yields the average colour) ---
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_SIZE, LUM_SIZE, { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false });
    this._lumScene = new THREE.Scene();
    this._lumUniforms = { uBuffer: { value: null } };
    this._lumQuad = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._lumUniforms, vertexShader: fullscreenVertexShader,
        fragmentShader: 'precision mediump float;\nuniform sampler2D uBuffer;\nvarying vec2 vUv;\nvoid main() { gl_FragColor = vec4(texture2D(uBuffer, vUv).rgb, 1.0); }',
        depthTest: false, depthWrite: false
      })
    );
    this._lumQuad.frustumCulled = false;
    this._lumScene.add(this._lumQuad);
    this._lumPixels = new Uint8Array(LUM_SIZE * LUM_SIZE * 4);

    // --- display ---
    const F = this._feedbackUniforms;
    this._displayUniforms = {
      uBuffer: { value: null },
      uAspect: { value: 16 / 9 }, uOffset: F.uOffset, uVertigo: F.uVertigo, uBreath: F.uBreath, uZoom: { value: 1 },
      uRot: F.uRot, uAngle: F.uAngle, uMirror: F.uMirror, uMirrorR: F.uMirrorR, uVertexR: F.uVertexR,
      uIters: F.uIters, uDisc: F.uDisc,
      uPx: { value: 1 / 720 }, uSeamDebug: { value: this._seamDebug ? 1 : 0 }
    };
    this._displayMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._displayUniforms, vertexShader: fullscreenVertexShader,
        fragmentShader: displayFragmentShader(this._precision, MAX_ITERS, layout.joints && !this._noJoints),
        depthTest: false, depthWrite: false
      })
    );
    this._displayMesh.frustumCulled = false;
    this._displayMesh.renderOrder = -1000;
    const self = this;
    this._displayMesh.onBeforeRender = function (renderer) { self._runPasses(renderer); };
    scene.add(this._displayMesh);

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._decay = this._forcedDecay != null ? this._forcedDecay : DECAY_DEFAULT;
    this._decayCeiling = Math.max(this._decay, DECAY_MAX);
    this._governorHot = false;
    this._frameCount = 0;
    this._lastLum = 0;
    this._offset = 0;
    this._scrolled = 0;          // the unwrapped distance, for the tests
    this._scrollDir = 1;
    this._rot = 0; this._rotFrom = 0; this._rotTo = 0; this._rotT = 1;
    this._vertigoFrom = 0; this._vertigoT = 1e9; this._vertigoAmp = 0; this._vertigoUp = 1; this._vertigoDown = 3;
    this._lastPhraseIndex = null;
    this._lastBeatIndex = null;
    this._lastBarIndex = null;
    this._lowSmooth = 0.5; this._midSmooth = 0.5; this._highSmooth = 0.5;
    this._hueDir = 1;
    this._palette = [new THREE.Color(0x39a0ff), new THREE.Color(0xff3ba7), new THREE.Color(0xffd23b)];
    this._lastPaletteRef = undefined;
    this._lastTrackId = undefined;
    this._lastArtworkUrl = undefined;
    this._coldStart = true;
    this._nextSeedAt = 0;
    this._fadeUntil = -1;
    this._fading = false;
    this._shapeUntil = -1;
    this._reseedUntil = -1;
    this._dropUntil = -1;
    this._lastDropTime = -1e9;
    this._bpm = null;
    this._beatPhase = 0;
    this._setTiling(this._forcedTiling || DEFAULT_TILING);

    if (this._lumDebug) {
      const el = document.createElement('div');
      el.style.cssText = 'position:fixed;left:10px;top:10px;z-index:99999;font:12px/1.5 ui-monospace,monospace;color:#9fe;background:rgba(0,0,0,.65);padding:6px 9px;border-radius:6px;white-space:pre';
      document.body.appendChild(el);
      this._lumEl = el;
    }
  }

  _makeBar(halfX, halfY) {
    const m = 0.02;
    const mat = new THREE.ShaderMaterial({
      uniforms: { uColor: { value: new THREE.Color(0xffffff) }, uOpacity: { value: 1 }, uHalfSize: { value: new THREE.Vector2(halfX + m, halfY + m) }, uRect: { value: new THREE.Vector2(halfX, halfY) }, uPx: { value: 1 / 360 } },
      vertexShader: shapeVertexShader, fragmentShader: barFragmentShader,
      blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false
    });
    this._shapeMaterials.push(mat);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry((halfX + m) * 2, (halfY + m) * 2), mat);
    mesh.frustumCulled = false;
    return mesh;
  }
  _makeDisc(inner, outer) {
    const half = outer * 1.2;
    const mat = new THREE.ShaderMaterial({
      uniforms: { uColor: { value: new THREE.Color(0xffffff) }, uOpacity: { value: 1 }, uHalfSize: { value: new THREE.Vector2(half, half) }, uInner: { value: inner }, uOuter: { value: outer }, uPx: { value: 1 / 360 } },
      vertexShader: shapeVertexShader, fragmentShader: discFragmentShader,
      blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false
    });
    this._shapeMaterials.push(mat);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(half * 2, half * 2), mat);
    mesh.frustumCulled = false;
    return mesh;
  }

  // Echo's governor, unchanged apart from the prefix, plus the mean colour
  // the fold hands to the pixels past its iteration budget.
  _measureLuminance(renderer) {
    this._lumUniforms.uBuffer.value = this._buffer.write.texture;
    renderer.setRenderTarget(this._lumTarget);
    renderer.render(this._lumScene, this._ortho);
    renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_SIZE, LUM_SIZE, this._lumPixels);
    let sum = 0, r = 0, g = 0, b = 0;
    for (let i = 0; i < this._lumPixels.length; i += 4) {
      r += this._lumPixels[i]; g += this._lumPixels[i + 1]; b += this._lumPixels[i + 2];
      sum += 0.299 * this._lumPixels[i] + 0.587 * this._lumPixels[i + 1] + 0.114 * this._lumPixels[i + 2];
    }
    const n = LUM_SIZE * LUM_SIZE;
    // past the iteration budget a pixel reads the mean colour: the sub-pixel
    // fringe at the band's edge (and at the edge the vertigo carries across
    // the frame) blends into its neighbours instead of drawing a dark stripe
    this._feedbackUniforms.uAverage.value.set(r / (255 * n), g / (255 * n), b / (255 * n)).multiplyScalar(this._decay);
    const lum = sum / (255 * n);
    this._lastLum = lum;
    if (lum > GOVERN_HIGH) this._governorHot = true;
    if (this._governorHot) {
      if (lum > GOVERN_RESUME) {
        const before = this._decay;
        this._decay = Math.max(DECAY_MIN, this._decay - GOVERN_STEP);
        if (this._decay !== before) console.log('[hyperbolic] governor: mean luminance ' + lum.toFixed(3) + ' -> decay ' + before.toFixed(3) + ' -> ' + this._decay.toFixed(3));
      } else {
        this._governorHot = false;
      }
    } else if (lum < GOVERN_LOW && this._decay < Math.min(this._decayCeiling, DECAY_MAX)) {
      const before = this._decay;
      this._decay = Math.min(Math.min(this._decayCeiling, DECAY_MAX), this._decay + GOVERN_STEP);
      console.log('[hyperbolic] governor: mean luminance ' + lum.toFixed(3) + ' -> decay ' + before.toFixed(3) + ' -> ' + this._decay.toFixed(3));
    }
  }

  _runPasses(renderer) {
    const rebuilt = this._buffer.ensureSize(renderer);
    this._source.ensureSize(renderer);
    if (rebuilt) {
      this._displayUniforms.uBuffer.value = this._buffer.read.texture;
      const aspect = this._buffer.width / Math.max(1, this._buffer.height);
      this._ortho.left = -0.5 * aspect; this._ortho.right = 0.5 * aspect;
      this._ortho.updateProjectionMatrix();
      this._feedbackUniforms.uAspect.value = aspect;
      this._displayUniforms.uAspect.value = aspect;
      const px = 1 / Math.max(1, this._buffer.height);
      for (const mat of this._shapeMaterials) mat.uniforms.uPx.value = px;
    }
    renderer.getDrawingBufferSize(_size);
    this._displayUniforms.uPx.value = 1 / Math.max(1, _size.y);
    if (this._paused) { this._displayUniforms.uBuffer.value = this._buffer.read.texture; return; }
    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    renderer.setRenderTarget(this._source.write);
    renderer.autoClear = false;
    renderer.clear(true, false, false);
    renderer.render(this._injectScene, this._ortho);
    this._feedbackUniforms.uPrevious.value = this._buffer.read.texture;
    this._feedbackUniforms.uSource.value = this._source.read.texture;
    this._feedbackUniforms.uDecay.value = this._fading ? FADE_DECAY : this._decay;
    this._feedbackUniforms.uSeed.value = (this._frameCount * 13.13) % 64;
    renderer.setRenderTarget(this._buffer.write);
    renderer.render(this._feedbackScene, this._ortho);
    this._frameCount++;
    if (!this._fading && this._frameCount % GOVERN_EVERY_FRAMES === 0) this._measureLuminance(renderer);
    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;
    this._buffer.swap();
    this._displayUniforms.uBuffer.value = this._buffer.read.texture;
    if (this._lumEl && this._frameCount % 10 === 0) {
      this._lumEl.textContent = 'hyperbolic  lum ' + this._lastLum.toFixed(3) + '\n        decay ' + this._decay.toFixed(3) + '\n        {' + this._tiling + '}  offset ' + this._offset.toFixed(2) + (this._governorHot ? '\n        GOVERNOR ACTIVE' : '');
    }
  }

  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors && palette.vividColors.length) ? palette.vividColors : [0x39a0ff, 0xff3ba7, 0xffd23b];
    for (let i = 0; i < 3; i++) this._palette[i].set(vivid[i % vivid.length]);
    const hsl = { h: 0, s: 0, l: 0 };
    this._palette[0].getHSL(hsl);
    this._hueDir = hsl.h >= 0.5 ? -1 : 1;
  }

  _setTiling(name) {
    const [p, q] = TILINGS[name];
    const T = buildTiling(p, q);
    this._tiling = name;
    this._T = T;
    const F = this._feedbackUniforms;
    F.uAngle.value = T.angle;
    F.uMirror.value.set(T.centre[0], T.centre[1]);
    F.uMirrorR.value = T.rho;
    F.uVertexR.value = T.rB;
    // the central cell's width, in band units (the polygon's inradius twice)
    // and in frame-height units for the injection
    this._cellBand = 2 * T.AC;
    this._cellFrame = this._cellBand / Math.PI;
  }

  _spawnShape() {
    const kind = Math.floor(Math.random() * 3);
    const color = this._palette[Math.floor(Math.random() * 3)];
    const mesh = kind === 0 ? this._bar : (kind === 1 ? this._ring : this._dot);
    for (const m of this._shapes) m.visible = false;
    mesh.visible = true;
    // inside the fundamental triangle: an angle under PI/p, a radius under
    // the polygon's inradius - the fold reads only that triangle back, and
    // every cell on screen is its image, so the shape lands in all of them
    const a = (0.15 + Math.random() * 0.7) * this._T.angle;
    const rad = this._cellFrame * (0.12 + Math.random() * 0.3);
    mesh.position.set(Math.cos(a) * rad, Math.sin(a) * rad, 0);
    mesh.scale.setScalar(this._cellFrame * 1.5);
    mesh.rotation.z = Math.random() * Math.PI;
    mesh.material.uniforms.uColor.value.copy(color);
    mesh.material.uniforms.uOpacity.value = 1.0;
    this._shapeUntil = this._time + SHAPE_LIFE_S;
  }

  _startVertigo(amp, upBeats, downBeats) {
    this._vertigoFrom = this._feedbackUniforms.uVertigo.value;
    this._vertigoAmp = amp;
    this._vertigoUp = upBeats; this._vertigoDown = downBeats;
    this._vertigoT = 0;
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;
    this._time += deltaTime;

    const director = profile.director;
    const bands = director && director.bands;
    const graded = !!(director && director.hasReliableGrid) && bands;
    const low = graded && bands.low != null ? clamp01(bands.low) : 0.5;
    const mid = graded && bands.mid != null ? clamp01(bands.mid) : 0.5;
    const high = graded && bands.high != null ? clamp01(bands.high) : 0.5;
    const k = 1 - Math.exp(-deltaTime / BAND_TAU);
    this._lowSmooth += (low - this._lowSmooth) * k;
    this._midSmooth += (mid - this._midSmooth) * k;
    this._highSmooth += (high - this._highSmooth) * k;
    this._bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || null;
    const spb = this._bpm ? 60 / this._bpm : 0.5;

    if (profile.artworkPalette !== this._lastPaletteRef) { this._lastPaletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    if (profile.trackId !== this._lastTrackId) {
      const first = this._lastTrackId === undefined;
      this._lastTrackId = profile.trackId;
      if (!first) this._fadeUntil = this._time + TRACK_FADE_S;
    }
    const fading = this._time < this._fadeUntil;
    this._fading = fading;

    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) {
        const self = this;
        loadArtworkTexture(profile.artworkUrl, { wrap: THREE.ClampToEdgeWrapping, mipmaps: false }, function (tex) {
          if (self._artTexture) self._artTexture.dispose();
          self._artTexture = tex;
          self._artMesh.material.map = tex;
          self._artMesh.material.needsUpdate = true;
          self._artMesh.visible = true;
        }, function () {});
      } else {
        this._artMesh.visible = false;
      }
    }

    // --- drops: the full vertigo, the edge crosses the frame ---------------------
    const nextEvent = director && director.nextEvent;
    if (nextEvent && (nextEvent.type === 'drop' || nextEvent.type === 'rise') && nextEvent.msUntil != null) {
      const until = nextEvent.msUntil / 1000;
      if (until <= 0.12 && this._time - this._lastDropTime > 2 * spb) {
        this._lastDropTime = this._time;
        this._startVertigo(VERTIGO_DROP, 1, DROP_DOWN_BARS * 4);
        this._dropUntil = this._time + (1 + DROP_DOWN_BARS * 4) * spb;
      }
    }
    const dropping = this._time < this._dropUntil;

    // --- musical events ---------------------------------------------------------
    const beatIndex = profile.beatIndex;
    if (this._coldStart && !fading && this._time >= this._nextSeedAt) { this._nextSeedAt = this._time + 0.7; this._spawnShape(); }
    if (beatIndex != null && this._lastBeatIndex != null && beatIndex !== this._lastBeatIndex && !fading) {
      this._coldStart = false;
      this._spawnShape();
      // a notch of rotation about the vertex: 2 PI / q, a symmetry of the tiling
      this._rotFrom = this._rot;
      this._rotTo += Math.PI * 2 / this._T.q;
      this._rotT = 0;
      const barIndex = profile.barIndex;
      if (barIndex != null && barIndex !== this._lastBarIndex) {
        this._reseedUntil = this._time + RESEED_LIFE_S;
        if (this._layout.vertigo && !dropping) this._startVertigo(VERTIGO_BAR, VERTIGO_UP_BEATS, VERTIGO_DOWN_BEATS);
      }
      this._lastBarIndex = profile.barIndex;
    } else if (this._lastBarIndex === null) {
      this._lastBarIndex = profile.barIndex;
    }
    this._lastBeatIndex = beatIndex;
    if (this._vertigoDebug && this._time > 1.5 && this._vertigoT > 1e8) this._startVertigo(VERTIGO_BAR, 1, 3);

    // --- phrases: a new tiling, the scroll turns round --------------------------------
    const phraseIndex = profile.phraseIndex;
    if (!dropping && phraseIndex != null && this._lastPhraseIndex != null && phraseIndex !== this._lastPhraseIndex) {
      const progress = profile.duration > 0 ? clamp01(profile.progress) : (director && director.intensity != null ? director.intensity : 0.5);
      const mid2 = progress > 0.2 && progress < 0.8;
      if (this._forcedTiling == null) {
        const pool = this._layout.tilings.filter((t) => mid2 || CALM_TILINGS.indexOf(t) !== -1);
        const pick = (pool.length ? pool : this._layout.tilings).filter((t) => t !== this._tiling);
        if (pick.length) this._setTiling(pick[Math.floor(Math.random() * pick.length)]);
      }
      this._scrollDir = -this._scrollDir;
    }
    this._lastPhraseIndex = phraseIndex;

    if (this._time >= this._shapeUntil) {
      const hold = !this._artTexture && !fading;
      for (const m of this._shapes) {
        if (hold && m.visible) m.material.uniforms.uOpacity.value = SHAPE_HOLD_OPACITY;
        else m.visible = false;
      }
    }
    const reseeding = this._time < this._reseedUntil;
    this._artMesh.visible = !!this._artTexture && !fading;
    this._artMesh.material.opacity = reseeding ? ART_RESEED_OPACITY : this._artOpacity;
    if (fading) { for (const m of this._shapes) m.visible = false; }

    // --- the transforms ----------------------------------------------------------------
    const F = this._feedbackUniforms;
    if (this._freeze) { F.uOffset.value = 0; F.uRot.value = 0; F.uBreath.value = 1; F.uVertigo.value = 0; F.uZoom.value = ZOOM_PER_CELL; this._vertigoT = 1e9; return; }
    // the scroll: one central cell per bar, bands.low +-50%, the direction by phrase
    const speed = this._cellBand * SCROLL_CELLS_PER_BAR / (4 * spb) * (1 + LOW_SWING * (this._lowSmooth * 2 - 1));
    this._scrolled += speed * this._scrollDir * deltaTime;
    this._offset += speed * this._scrollDir * deltaTime;
    // wrapped by the tiling's own period along the axis: invisible, and it
    // keeps the band coordinate within a float's reach
    const P = this._T.period;
    this._offset = ((this._offset + P / 2) % P + P) % P - P / 2;
    F.uOffset.value = this._offset;
    // the rotation notch, over 120 ms
    this._rotT += deltaTime;
    this._rot = lerp(this._rotFrom, this._rotTo, smooth(this._rotT / ROT_STEP_S));
    F.uRot.value = this._rot;
    // the breath: the band's height, +-3% with beatPhase, bands.mid on the amplitude
    const bp = profile.beatPhase != null ? profile.beatPhase : (this._time / spb) % 1;
    this._beatPhase = bp;
    const dig = Math.exp(-bp * BREATH_DECAY);
    F.uBreath.value = 1 + BREATH * (0.5 + this._midSmooth) * (dig * 2 - 1);
    // the vertigo: up over its beats, back over the others
    this._vertigoT += deltaTime;
    const up = this._vertigoUp * spb, down = this._vertigoDown * spb;
    let v = 0;
    if (this._vertigoT < up) v = lerp(this._vertigoFrom, this._vertigoAmp, smooth(this._vertigoT / up));
    else if (this._vertigoT < up + down) v = this._vertigoAmp * (1 - smooth((this._vertigoT - up) / down));
    F.uVertigo.value = v;
    F.uZoom.value = ZOOM_PER_CELL;

    // the cover in the central cell, off its centre
    const cw = this._cellFrame;
    this._artMesh.scale.set(ART_SIZE * cw, ART_SIZE * cw, 1);
    this._artMesh.position.set(cw * 0.18, cw * 0.12, 0);

    hueMatrix(HUE_SHIFT * (0.6 + 0.8 * this._highSmooth) * Math.PI * 2 * this._hueDir, F.uHueMat.value);
  }

  // --- for the tests --------------------------------------------------------------
  get state() { return { tiling: this._tiling, T: this._T, period: this._T.period, offset: this._offset, scrolled: this._scrolled, rot: this._rot, rotTo: this._rotTo, vertigo: this._feedbackUniforms.uVertigo.value, breath: this._feedbackUniforms.uBreath.value, scrollDir: this._scrollDir, decay: this._decay, cellBand: this._cellBand }; }

  dispose() {
    this._scene.remove(this._displayMesh);
    this._displayMesh.geometry.dispose();
    this._displayMesh.material.dispose();
    this._feedbackQuad.geometry.dispose();
    this._feedbackQuad.material.dispose();
    for (const m of this._shapes.concat([this._artMesh])) { m.geometry.dispose(); m.material.dispose(); }
    this._shapeMaterials.length = 0;
    this._lumQuad.geometry.dispose();
    this._lumQuad.material.dispose();
    this._lumTarget.dispose();
    this._buffer.dispose();
    this._source.dispose();
    if (this._artTexture) this._artTexture.dispose();
    if (this._lumEl && this._lumEl.parentNode) this._lumEl.parentNode.removeChild(this._lumEl);
  }
}

const _size = new THREE.Vector2();
